import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import RoundDetailsModal from '../components/rounds/RoundDetailsModal';
import { roundsAPI, scoresAPI } from '../services/api';
import { getRoundLabel } from '../utils/roundHelpers';

export default function EventsHistory() {
  const navigate = useNavigate();

  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [selectedRoundDetails, setSelectedRoundDetails] = useState(null);
  const [selectedRoundLabel, setSelectedRoundLabel] = useState(null);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchCompletedRounds();
  }, []);

  const fetchCompletedRounds = async () => {
    try {
      setLoading(true);
      setError('');
      // Get all completed rounds (practice and championship)
      const filters = { status: 'completed' };
      const data = await roundsAPI.getAll(filters);
      setRounds(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading rounds:', err);
      setError('Failed to load event history');
      setRounds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (roundId, roundLabel) => {
    try {
      setError('');
      const [roundDetails, leaderboard] = await Promise.all([
        roundsAPI.getById(roundId),
        roundsAPI.getLeaderboard(roundId)
      ]);

      setSelectedRoundId(roundId);
      setSelectedRoundDetails(roundDetails);
      setSelectedRoundLabel(roundLabel || null);
      setSelectedLeaderboard(leaderboard);
      setShowModal(true);
    } catch (err) {
      console.error('Error loading round details:', err);
      setError('Failed to load round details');
    }
  };

  const handleViewScorecard = async (roundPlayerId) => {
    try {
      const scorecard = await scoresAPI.getScorecard(roundPlayerId);
      return scorecard;
    } catch (err) {
      console.error('Error loading scorecard:', err);
      throw err;
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRoundId(null);
    setSelectedRoundDetails(null);
    setSelectedLeaderboard(null);
  };

  // Group rounds by event_name
  const groupEventData = () => {
    const eventMap = new Map();

    rounds.forEach(round => {
      if (round.event_name) {
        if (!eventMap.has(round.event_name)) {
          eventMap.set(round.event_name, {
            eventName: round.event_name,
            courseName: round.course_name,
            courseLocation: round.course_location,
            rounds: []
          });
        }
        eventMap.get(round.event_name).rounds.push(round);
      }
    });

    // Sort rounds within each event
    eventMap.forEach(event => {
      event.rounds.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    });

    return eventMap;
  };

  const eventMap = groupEventData();

  // Sort events by most recent first
  const sortedEvents = Array.from(eventMap.entries())
    .sort((a, b) => {
      const dateA = new Date(a[1].rounds[0].start_date);
      const dateB = new Date(b[1].rounds[0].start_date);
      return dateB - dateA;
    });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full pb-20">
      {/* Header */}
      <div className="mb-4 px-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Results History</h1>
        <button
          onClick={() => navigate(-1)}
          className="text-primary font-semibold flex items-center gap-2 hover:underline text-base"
        >
          <span>←</span>
          <span>Back to Menu</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-600 text-white px-4 py-3 mb-4 mx-4 shadow-lg">
          <div className="flex items-start gap-2">
            <span className="text-xl">⚠️</span>
            <div className="flex-1">
              <p className="font-semibold mb-1">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {sortedEvents.length === 0 ? (
        <div className="px-4">
          <div className="bg-white border border-gray-300 rounded-lg p-8">
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏆</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No Completed Events</h2>
              <p className="text-lg text-gray-600">
                Completed events will appear here once rounds are finished.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedEvents.map(([eventName, event]) => {
            // Find the overall event winner (highest points across all rounds)
            const eventWinner = event.rounds.reduce((best, round) => {
              if (!round.leading_points || round.leading_points === 0) return best;
              if (!best || round.leading_points > best.leading_points) {
                return {
                  name: round.leading_player_name,
                  points: round.leading_points
                };
              }
              return best;
            }, null);

            return (
              <div key={eventName} className="bg-white border border-gray-300 mx-4">
                {/* Event Header */}
                <div className="border-b border-gray-300 px-4 py-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h2 className="text-xl font-bold text-primary">{event.eventName}</h2>
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                      COMPLETED
                    </span>
                  </div>
                  <p className="text-base font-semibold text-gray-700">{event.courseName}</p>
                  <p className="text-sm text-gray-600">{event.courseLocation}</p>
                  {eventWinner && (
                    <div className="mt-3 inline-block bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 border-yellow-300 rounded-lg px-4 py-2">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🏆</span>
                        <div>
                          <p className="text-sm text-yellow-800 font-medium">Event Winner</p>
                          <p className="text-lg font-bold text-yellow-900">
                            {eventWinner.name} - {eventWinner.points} points
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Rounds */}
                <div>
                  {event.rounds.map((round, roundIndex) => {
                    const roundLabel = getRoundLabel(roundIndex, event.rounds.length);

                    return (
                      <div key={round.id} className="flex items-center justify-between px-4 py-4 border-b border-gray-200 last:border-0 hover:bg-gray-50">
                        <div className="flex-1">
                          {roundLabel && (
                            <div className="mb-1">
                              <span className="text-base font-semibold text-gray-900">
                                {roundLabel}
                              </span>
                            </div>
                          )}
                          {round.leading_player_name && round.leading_points > 0 && (
                            <p className="text-sm text-gray-700 mb-1">
                              Winner: <span className="font-semibold">{round.leading_player_name}</span> ({round.leading_points} pts)
                            </p>
                          )}
                          <p className="text-sm text-gray-500">
                            {new Date(round.start_date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                        <button
                          onClick={() => handleViewDetails(round.id, roundLabel)}
                          className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-secondary transition-colors min-h-touch-lg"
                        >
                          View Results
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Round Details Modal */}
      <RoundDetailsModal
        round={selectedRoundDetails}
        roundLabel={selectedRoundLabel}
        leaderboard={selectedLeaderboard}
        isOpen={showModal}
        onClose={handleCloseModal}
        onViewScorecard={handleViewScorecard}
      />
    </div>
  );
}
